import { ApolloError } from '@apollo/client'

import { useLazyAlternativeProducts } from '../../graphQL/queries/alternativeProducts'
import {
  IAlternativeProductsResponse,
  IAlternativeProductsVariables,
} from '../../graphQL/queries/alternativeProducts/types'
import {
  setAlternativeProductsItems,
  setAlternativeProductsStatus,
} from '../../redux/slices/recommendations'
import { IProduct } from '../../interfaces'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  handleRecommendationFetchError,
  mergeCartQuantitiesIntoRecommendationItems,
  skipIfMissingIdentifiers,
} from './recommendationHookHelpers'

const FUNCTION_NAME = 'useAlternativeProducts'

/**
 * Sorts alternative products by price ascending (cheapest first). Items with a
 * missing / non-numeric price sink to the end so a null price never masquerades
 * as the cheapest option. The sort is stable — equal-priced items keep their
 * source order — so the UI stays deterministic across re-fetches.
 */
const sortByPriceAscending = (items: IProduct[]): IProduct[] => {
  const withPrice = (product: IProduct): number =>
    typeof product.price === 'number' && Number.isFinite(product.price)
      ? product.price
      : Number.POSITIVE_INFINITY

  return [...items].sort((a, b) => withPrice(a) - withPrice(b))
}

/**
 * Hook that fetches the `alternativeProducts` ("Opciones similares") carousel
 * data for the displayed product-detail SKUs and writes it into the matching
 * Redux slice. Triggered from the EventManager via the
 * `templateActions.alternativeProducts.fetchProducts` template action.
 *
 * Unlike the recommendation carousels, the response is a flat `IProduct[]`
 * (no `{ items }` wrapper) and the items are sorted by price ascending before
 * being dispatched so the cheapest option renders first (leftmost in LTR).
 */
export const useAlternativeProducts = () => {
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.defaultSlice.config)
  const cartItems = useAppSelector((state) => state.cartSlice.items)
  const cartGroups = useAppSelector((state) => state.cartSlice.groups)
  const cartActivePromotions = useAppSelector(
    (state) => state.cartSlice.activePromotions,
  )

  const onCompleted = ({
    alternativeProducts,
  }: IAlternativeProductsResponse) => {
    // Apollo invokes onCompleted on a detached microtask whose throws are
    // silently swallowed in production builds. Wrap the merge + sort + dispatch
    // so any throw surfaces as ERROR + Datadog instead of leaving the slice
    // permanently in LOADING.
    try {
      const merged = mergeCartQuantitiesIntoRecommendationItems(
        alternativeProducts,
        {
          items: cartItems,
          groups: cartGroups ?? [],
          activePromotions: cartActivePromotions,
        },
        config,
      )
      dispatch(setAlternativeProductsItems(sortByPriceAscending(merged)))
      dispatch(setAlternativeProductsStatus('OK'))
    } catch (exception) {
      dispatch(setAlternativeProductsStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'alternativeProducts onCompleted threw during cart-quantity merge',
      )
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(setAlternativeProductsStatus('ERROR'))
    handleRecommendationFetchError(
      error,
      FUNCTION_NAME,
      'alternativeProducts fetch failed',
    )
  }

  const [getAlternativeProducts] = useLazyAlternativeProducts({
    onCompleted,
    onError,
  })

  const fetchAlternativeProducts = async (
    variables: IAlternativeProductsVariables,
  ) => {
    // SKU-keyed carousel: drop the previous product's items immediately so a
    // failed or skipped fetch can never leave another product's alternatives on
    // screen. setItems([]) also flips hideCarousel: true (createCarouselSlice),
    // hiding the carousel during load and after failures — the failure UX here,
    // since nothing binds `status` (no "Ver todos" list like the siblings). It
    // leaves `status` untouched, so the skip guards' "don't flip to ERROR"
    // contract still holds and a later valid call proceeds. Trade-off: with
    // no-cache, revisiting a product blanks then repopulates rather than showing
    // stale items — correct for a SKU-keyed carousel (prices/promos change).
    dispatch(setAlternativeProductsItems([]))

    if (skipIfMissingIdentifiers(variables, FUNCTION_NAME, 'alternativeProducts'))
      return
    // {{params.sku}} resolves to `undefined` (not `[]`) before the product-detail
    // data loads, so the config binding arrives as `skus: [undefined]`. Filter to
    // real SKUs and skip when none remain — without flipping to ERROR so a later
    // valid call can proceed.
    const skus = (variables.skus ?? []).filter(Boolean)
    if (skus.length === 0) {
      handleRecommendationFetchError(
        new Error('called with empty skus'),
        FUNCTION_NAME,
        'alternativeProducts fetch skipped — missing skus',
      )
      return
    }
    dispatch(setAlternativeProductsStatus('LOADING'))
    try {
      await getAlternativeProducts({ variables: { ...variables, skus } })
    } catch (exception) {
      dispatch(setAlternativeProductsStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'alternativeProducts fetch threw synchronously',
      )
    }
  }

  return fetchAlternativeProducts
}
